import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Authentication state interface
 */
export interface CredState {
  // Login/Register flow state
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  login_email: string;
  login_password: string;
  phone: string;
  confirm_password: string;
  date_of_birth: string;

  // Loading and error states
  isLoading: boolean;
  error: string | null;

  // Authentication status
  isAuthenticated: boolean;
  customer: any | null;

  customerProfileData: any | null;

  // Profile stats
  stats: {
    orders: number;
    addresses: number;
  };
  statsLoading: boolean;
}

/**
 * Initial state for authentication
 */
const initialState: CredState = {
  first_name: "",
  last_name: "",
  email: "",
  password: "",
  login_email: "",
  login_password: "",
  phone: "",
  confirm_password: "",
  date_of_birth: "",
  isLoading: false,
  error: null,
  isAuthenticated: false,
  customer: null,
  customerProfileData: null,
  stats: {
    orders: 0,
    addresses: 0,
  },
  statsLoading: false,
};

/**
 * Auth slice for managing authentication state
 */
const credSlice = createSlice({
  name: "cred",
  initialState,
  reducers: {
    setFirstName: (state, action: PayloadAction<string>) => {
      state.first_name = action.payload;
    },
    setLastName: (state, action: PayloadAction<string>) => {
      state.last_name = action.payload;
    },
    setPhone: (state, action: PayloadAction<string>) => {
      state.phone = action.payload;
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },
    setPassword: (state, action: PayloadAction<string>) => {
      state.password = action.payload;
    },
    setLoginEmail: (state, action: PayloadAction<string>) => {
      state.login_email = action.payload;
    },
    setLoginPassword: (state, action: PayloadAction<string>) => {
      state.login_password = action.payload;
    },
    setConfirmPassword: (state, action: PayloadAction<string>) => {
      state.confirm_password = action.payload;
    },
    setDateOfBirth: (state, action: PayloadAction<string>) => {
      state.date_of_birth = action.payload;
    },

    // Loading state management
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    // Error management
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Login/Registration start
    loginRegistrationStart: (state) => {
      state.isLoading = true;
      state.error = null;
    },

    // Login success
    loginRegistrationSuccess: (state, action: PayloadAction<any>) => {
      state.isLoading = false;
      state.error = null;
      state.isAuthenticated = true;
      state.customer = action.payload;
    },

    // Login failure
    loginRegistrationFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // Clear login form (reset all fields)
    clearLoginForm: (state) => {
      state.error = null;
      state.isLoading = false;
      state.login_email = "";
      state.login_password = "";
    },

    // Clear Registration form (reset all fields)
    clearRegistrationForm: (state) => {
      state.error = null;
      state.isLoading = false;
      state.first_name = "";
      state.last_name = "";
      state.phone = "";
      state.email = "";
      state.password = "";
      state.confirm_password = "";
      state.date_of_birth = "";
    },

    // Logout
    logout: (state) => {
      state.isAuthenticated = false;
      state.customer = null;
      state.error = null;
      state.isLoading = false;
    },

    // Set customer profile data
    setCustomerProfileData: (state, action: PayloadAction<any | null>) => {
      state.customerProfileData = action.payload;
    },

    // Profile stats initialization start
    initializeStatsStart: (state) => {
      state.statsLoading = true;
    },

    // Profile stats initialization success
    initializeStatsSuccess: (
      state,
      action: PayloadAction<{ orders: number; addresses: number }>,
    ) => {
      state.statsLoading = false;
      state.stats = action.payload;
    },

    // Profile stats initialization failure
    initializeStatsFailure: (state) => {
      state.statsLoading = false;
    },
  },
});

// Export actions
export const {
  setFirstName,
  setLastName,
  setEmail,
  setPassword,
  setConfirmPassword,
  setDateOfBirth,
  setPhone,
  setLoading,
  setError,
  clearError,
  loginRegistrationStart,
  loginRegistrationSuccess,
  loginRegistrationFailure,
  clearLoginForm,
  logout,
  setCustomerProfileData,
  clearRegistrationForm,
  setLoginEmail,
  setLoginPassword,
  initializeStatsStart,
  initializeStatsSuccess,
  initializeStatsFailure,
} = credSlice.actions;

// Export reducer
export default credSlice.reducer;
